import { useState } from 'react';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { PayerKnowledge } from '../api/payersApi';
import styles from './PayerKnowledgePanel.module.css';

function yesNo(value: boolean | null): string {
  if (value === null) return 'Unknown';
  return value ? 'Yes' : 'No';
}

interface Chip {
  label: string;
  warn?: boolean;
}

// Only the facts worth a glance — the full field list is one click away in
// "all fields". A payer with 10+ knowledge fields shown flat at all times
// buries the handful that actually matter day to day.
function buildChips(k: PayerKnowledge): Chip[] {
  const chips: Chip[] = [];

  if (k.authentication.providerNpiRequired) chips.push({ label: 'NPI required' });
  chips.push({ label: k.authentication.providerTinRequired ? 'TIN required' : 'TIN not asked' });
  chips.push({
    label: k.authentication.providerLicenseRequired ? 'License required' : 'License not asked',
  });
  if (k.authentication.memberIdRequired) chips.push({ label: 'Member ID required' });

  if (k.behavior.speechSupported === false) chips.push({ label: 'DTMF only' });
  else if (k.behavior.speechSupported === true) chips.push({ label: 'Speech + keypad' });
  else chips.push({ label: 'Speech: untested' });

  chips.push(
    k.behavior.neverPress0 ? { label: 'Press 0: avoid', warn: true } : { label: 'Press 0: safe' },
  );

  chips.push(
    k.representative.everReached
      ? { label: 'Representative reached' }
      : { label: 'Representative: never reached', warn: true },
  );

  chips.push({
    label: k.representative.callbackEverOffered ? 'Callback offered' : 'Callback: none',
  });

  return chips;
}

interface PayerKnowledgePanelProps {
  knowledge: PayerKnowledge | null;
  version: number | null;
}

// Read-only view of the payer's synthesized operational facts — lives in
// the same version row as the navigation map (see backend charting/
// schemas.ts PayerKnowledgeSchema). No fetch of its own; the parent passes
// down whatever version's detail is currently loaded.
export function PayerKnowledgePanel({ knowledge: k, version }: PayerKnowledgePanelProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div className={styles.header} onClick={() => setExpanded((e) => !e)}>
        <span className={styles.headerLabel}>
          Payer knowledge{version ? ` — v${version}` : ''}
          {' · '}
          <span className={styles.headerHint}>rarely changes between versions</span>
        </span>
        <span className={styles.toggleLink}>{expanded ? 'hide' : 'all fields'} ▾</span>
      </div>

      {!k ? (
        <div className={patterns.gridTableEmpty}>No knowledge synthesized yet for this payer.</div>
      ) : (
        <>
          <div className={styles.chips}>
            {buildChips(k).map((chip) => (
              <span
                key={chip.label}
                className={chip.warn ? `${styles.chip} ${styles.chipWarn}` : styles.chip}
              >
                {chip.label}
              </span>
            ))}
          </div>

          {expanded && (
            <div className={styles.factGrid}>
              <span className={styles.factLabel}>Member DOB required</span>
              <span className={styles.factValue}>{yesNo(k.authentication.memberDobRequired)}</span>
              <span className={styles.factLabel}>Office ZIP required</span>
              <span className={styles.factValue}>{yesNo(k.authentication.officeZipRequired)}</span>
              <span className={styles.factLabel}>Provider info asked before member info</span>
              <span className={styles.factValue}>
                {yesNo(k.authentication.providerCredentialsBeforeMemberInfo)}
              </span>
              <span className={styles.factLabel}>Estimated wait stated</span>
              <span className={styles.factValue}>{k.behavior.estimatedWaitStated ?? '—'}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
